import React, { useState } from 'react';
import { Table, Button, Progress, Tag, Popconfirm, message, Tooltip, Spin } from 'antd';
import { CheckCircleOutlined, UndoOutlined } from '@ant-design/icons';
import { getHistoricalCourseStatus, completeCourseHistorical, undoCourseHistorical } from '../../api/students';
import { getStudentProgress } from '../../api/progress';
import { apiErrorMessage } from '../../utils/apiError';

const StudentHistoricalCompletionTab = ({ studentId, onUpdate }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState({});
  const [undoing, setUndoing] = useState({});
  const [topicsByCourse, setTopicsByCourse] = useState({});
  const [topicsLoading, setTopicsLoading] = useState({});

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await getHistoricalCourseStatus(studentId);
      setCourses(res.data || []);
    } catch {
      message.error('Programlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (studentId) fetchStatus();
  }, [studentId]);

  const fetchTopics = async (courseId) => {
    setTopicsLoading(prev => ({ ...prev, [courseId]: true }));
    try {
      const res = await getStudentProgress(studentId, courseId);
      setTopicsByCourse(prev => ({ ...prev, [courseId]: res.data?.topics || [] }));
    } catch {
      message.error('Konular yüklenemedi.');
    } finally {
      setTopicsLoading(prev => ({ ...prev, [courseId]: false }));
    }
  };

  const handleComplete = async (course, topicIds) => {
    const key = topicIds ? `${course.course_id}-${topicIds[0]}` : course.course_id;
    setCompleting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await completeCourseHistorical(studentId, course.course_id, topicIds);
      message.success(res.data?.message || 'İşlem tamamlandı.');
      await fetchStatus();
      await fetchTopics(course.course_id);
      onUpdate?.();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setCompleting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUndo = async (course, topicIds) => {
    const key = topicIds ? `${course.course_id}-${topicIds[0]}` : course.course_id;
    setUndoing(prev => ({ ...prev, [key]: true }));
    try {
      const res = await undoCourseHistorical(studentId, course.course_id, topicIds);
      message.success(res.data?.message || 'Geri alındı.');
      await fetchStatus();
      await fetchTopics(course.course_id);
      onUpdate?.();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Geri alma başarısız.'));
    } finally {
      setUndoing(prev => ({ ...prev, [key]: false }));
    }
  };

  const columns = [
    {
      title: 'Sertifika Programı',
      dataIndex: 'course_name',
      key: 'course_name',
    },
    {
      title: 'İlerleme',
      key: 'progress',
      render: (_, r) => (
        <Progress
          percent={r.total > 0 ? Math.round((r.completed / r.total) * 100) : 0}
          format={() => `${r.completed}/${r.total} konu`}
          status={r.completed >= r.total ? 'success' : 'active'}
          style={{ minWidth: 180 }}
        />
      ),
    },
    {
      title: 'Durum',
      key: 'status',
      render: (_, r) => r.has_certificate
        ? <Tag icon={<CheckCircleOutlined />} color="green">Sertifika verildi</Tag>
        : (r.completed >= r.total
          ? <Tag color="blue">Konular tamam</Tag>
          : <Tag>Devam ediyor</Tag>),
    },
    {
      title: 'İşlem',
      key: 'action',
      render: (_, r) => {
        const alreadyDone = r.completed >= r.total || r.has_certificate;
        if (!r.has_historical_classroom) {
          return (
            <Tooltip title='Bu program için "Geçmiş Dönem" adıyla başlayan bir derslik bulunamadı.'>
              <Button size="small" disabled onClick={(e) => e.stopPropagation()}>Tamamla</Button>
            </Tooltip>
          );
        }
        if (alreadyDone) {
          if (r.has_historical_completion) {
            return (
              <Popconfirm
                title="Geçmiş dönem tamamlamasını geri al"
                description={`${r.course_name} programı için geçmiş dönem üzerinden yapılan tüm tamamlamalar geri alınacak. Sertifika verilmişse iptal edilecek. Devam edilsin mi?`}
                onConfirm={() => handleUndo(r)}
                okText="Evet, geri al"
                cancelText="Vazgeç"
              >
                <Button
                  size="small"
                  danger
                  icon={<UndoOutlined />}
                  loading={undoing[r.course_id]}
                  onClick={(e) => e.stopPropagation()}
                >
                  Geri Al
                </Button>
              </Popconfirm>
            );
          }
          return <Button size="small" disabled onClick={(e) => e.stopPropagation()}>Tamamla</Button>;
        }
        return (
          <Popconfirm
            title="Programı geçmiş tarihli olarak tamamla"
            description={`${r.course_name} programındaki kalan ${r.total - r.completed} konu, ayrı geçmiş tarihlerle otomatik olarak tamamlanacak. Devam edilsin mi?`}
            onConfirm={() => handleComplete(r)}
            okText="Evet, tamamla"
            cancelText="Vazgeç"
          >
            <Button
              size="small"
              type="primary"
              loading={completing[r.course_id]}
              onClick={(e) => e.stopPropagation()}
            >
              Tamamla
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const expandedRowRender = (record) => {
    if (topicsLoading[record.course_id]) {
      return <Spin size="small" />;
    }

    const topics = topicsByCourse[record.course_id] || [];

    const topicColumns = [
      { title: 'Konu', dataIndex: 'topic_name', key: 'topic_name' },
      {
        title: 'Durum',
        key: 'status',
        render: (_, t) => t.status === 'completed'
          ? <Tag color="green">Tamamlandı</Tag>
          : <Tag>Girilmemiş</Tag>,
      },
      {
        title: 'İşlem',
        key: 'action',
        render: (_, t) => {
          const key = `${record.course_id}-${t.topic_id}`;
          if (t.status === 'completed') {
            if (!t.has_historical_completion) return null;
            return (
              <Popconfirm
                title="Konuyu geri al"
                description="Bu konu geçmiş dönem üzerinden tamamlanmıştı, geri alınacak. Programın sertifikası verilmişse iptal edilecek."
                onConfirm={() => handleUndo(record, [t.topic_id])}
                okText="Evet, geri al"
                cancelText="Vazgeç"
              >
                <Button size="small" danger icon={<UndoOutlined />} loading={undoing[key]}>Geri Al</Button>
              </Popconfirm>
            );
          }
          if (!record.has_historical_classroom) return null;
          return (
            <Popconfirm
              title="Konuyu geçmiş tarihli olarak tamamla"
              onConfirm={() => handleComplete(record, [t.topic_id])}
              okText="Evet"
              cancelText="Vazgeç"
            >
              <Button size="small" loading={completing[key]}>Tamamla</Button>
            </Popconfirm>
          );
        },
      },
    ];

    return (
      <Table
        size="small"
        rowKey="topic_id"
        dataSource={topics}
        columns={topicColumns}
        pagination={false}
        locale={{ emptyText: 'Konu bulunamadı.' }}
      />
    );
  };

  return (
    <>
      <p className="text-sm text-gray-500 mb-3">
        Kaynak dosyası olmayan geçmiş dönem verileri için: bir satıra tıklayınca konular
        açılır, tek tek veya üstteki "Tamamla" ile toplu olarak geçmiş tarihli tamamlanabilir
        (her konu ayrı bir güne denk gelir). Bir program yarıda kaldıysa sadece eksik
        konuları tamamlamak yeterlidir. Tüm konular bitince sertifika otomatik oluşur.
        Yanlışlıkla tamamlanan geçmiş dönem kayıtları "Geri Al" ile geri alınabilir —
        sertifika verilmişse iptal edilir; gerçek bir derste tamamlanan konulara bu
        ekrandan dokunulmaz.
      </p>
      <Table
        size="small"
        rowKey="course_id"
        dataSource={courses}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: 'Program bulunamadı.' }}
        expandable={{
          expandRowByClick: true,
          expandedRowRender,
          onExpand: (expanded, record) => {
            if (expanded && !topicsByCourse[record.course_id]) {
              fetchTopics(record.course_id);
            }
          },
        }}
      />
    </>
  );
};

export default StudentHistoricalCompletionTab;
